<script setup>
import { computed, inject } from 'vue';
import SuggestionList from '@/components/result/SuggestionList.vue';
import TrendLineChart from '@/components/result/TrendLineChart.vue';
import { template } from 'lodash';

// 부모에서 주입받은 데이터
const studentData = inject('studentData');

const region = "seoul";

// 학교급과 학년에 따른 gradeLevel 설정
const gradeLevel = computed(() => {
    if (!studentData.value) return 'E_4';
    
    const schoolType = studentData.value.schul_type;
    const grade = parseInt(studentData.value.grade_code);
    
    if (schoolType === 'E') {
        return grade === 4 ? 'E_4' : 'E_6';
    } else if (schoolType === 'M') {
        return 'M_2';
    } else if (schoolType === 'H') {
        return 'H_1';
    }
    
    return 'E_4'; // 기본값
});

// 기존 gradeGroup도 유지 (하위 호환성)
const gradeGroup = computed(() => {
    if (!studentData.value) return 'elementary';
    
    const schoolType = studentData.value.school_type;
    return schoolType === 'E' ? 'elementary' : 'secondary';
});

// 차트 데이터가 2개 이상 있는지 확인
const hasMultipleChartData = computed(() => {
    if (!studentData.value || !studentData.value.beforeScores) return false;
    
    const beforeScores = studentData.value.beforeScores;
    const currentData = studentData.value;
    
    // 이전 데이터 개수 + 현재 데이터(1개) >= 2
    return beforeScores.length + 1 >= 2;
});

// 차트 카테고리 (학년별)
const categories = ['초4', '초6', '중2', '고1'];

// 문해력 차트 데이터
const literacyChartData = computed(() => {
    if (!studentData.value) return [];
    
    const beforeScores = studentData.value.beforeScores || [];
    const currentData = studentData.value;
    
    // 모든 점수 데이터 (이전 + 현재) 수집
    const allScores = [...beforeScores];
    if (currentData.korscore) {
        allScores.push({
            grade_code: currentData.grade_code,
            korscore: currentData.korscore,
            exam_date: currentData.exam_date || new Date().toISOString()
        });
    }
    
    // 학년별로 그룹화하고 최신 점수만 유지
    const gradeScores = {};
    allScores.forEach(score => {
        if (score.korscore) {
            const grade = score.grade_code;
            if (!gradeScores[grade] || new Date(score.exam_date) > new Date(gradeScores[grade].exam_date)) {
                gradeScores[grade] = score;
            }
        }
    });
    
    // 차트 데이터 형식으로 변환
    const scores = categories.map(category => {
        const gradeKey = category === '초4' ? '4' : category === '초6' ? '6' : category === '중2' ? '2' : '1';
        return gradeScores[gradeKey]?.korscore || null;
    });
    
    return [{
        label: '문해력',
        scores: scores
    }];
});

// 수리력 차트 데이터
const numeracyChartData = computed(() => {
    if (!studentData.value) return [];
    
    const beforeScores = studentData.value.beforeScores || [];
    const currentData = studentData.value;
    
    // 모든 점수 데이터 (이전 + 현재) 수집
    const allScores = [...beforeScores];
    if (currentData.matscore) {
        allScores.push({
            grade_code: currentData.grade_code,
            matscore: currentData.matscore,
            exam_date: currentData.exam_date || new Date().toISOString()
        });
    }
    
    // 학년별로 그룹화하고 최신 점수만 유지
    const gradeScores = {};
    allScores.forEach(score => {
        if (score.matscore) {
            const grade = score.grade_code;
            if (!gradeScores[grade] || new Date(score.exam_date) > new Date(gradeScores[grade].exam_date)) {
                gradeScores[grade] = score;
            }
        }
    });
    
    // 차트 데이터 형식으로 변환
    const scores = categories.map(category => {
        const gradeKey = category === '초4' ? '4' : category === '초6' ? '6' : category === '중2' ? '2' : '1';
        return gradeScores[gradeKey]?.matscore || null;
    });
    
    return [{
        label: '수리력',
        scores: scores
    }];
});

</script>

<template>
    <div class="slide-inner" data-page-id="page6">
        <div class="content pt-[30px]">
            <SuggestionList 
                type="literacy"
                :region="region"
                :gradeGroup="gradeGroup"
                :gradeLevel="gradeLevel"
                :isStudent="true"
            />
            <SuggestionList type="numeracy" 
                :region="region" 
                :isStudent="true" 
                :gradeGroup="gradeGroup"
                :gradeLevel="gradeLevel"
                class="pt-[10px] pb-[10px]"
            />
            <div class="bg-gary">    
                <n-flex :wrap="false" justify="space-between" class="pt-2" :size="0" align="flex-start"> 
                    <div class="card">
                        <h5 class="text-[22px]">문해력 수리력 성장 그래프</h5>                        
                        <p class="text-[17px] pt-1 leading-tight">학생이 학년이 바뀌어 다시 이 검사를 치르게 된다면, 이전 결과와 비교하여 학생이 어느 정도 성장했는지 알려주는 그래프입니다.​</p>
                        <p v-if="hasMultipleChartData" class="caution">2023년 점수는 2024년 기준척도로 변환된 점수입니다.</p>
                        <p v-else class="caution">2024년 이후 응시한 진단검사 결과 데이터가 반영됩니다.</p>
                        <n-flex :wrap="false" justify="flex-end" align="center" :size="5" class="pb-1">
                            <img src="@/assets/images/result/tell.png" style="width: 14px;">
                            <p class="text-[14px]"><span class="literacy-color ">문의 : </span>서울특별시 교육청 학생역량혁신교육과(02-2282-8485)​</p>       
                        </n-flex>                                        
                        <n-flex :wrap="false" :size="20">
                            <TrendLineChart
                                :categories="categories"
                                :data="literacyChartData"
                                chartType="literacy"
                            />
                             <TrendLineChart
                                :categories="categories"
                                :data="numeracyChartData"
                                chartType="numeracy"
                            />
                        </n-flex>
                        <aside>
                            <p class="text-[16px] pt-3 pb-2 leading-tight"><span class="literacy-light-color font-semibold">서울 학생 문해력·수리력 진단검사 점수</span>는 초등학교 4학년부터 고등학교 1학년까지의 문해력·수리력 점수 범위를 1,000~2,000점이 되게 개발한 것입니다. 평균적으로 점수는 저학년일수록 낮고, 고학년일수록 높습니다. 학년별 점수 범위는 다음과 같습니다.​</p>
                            <n-flex class="table-wrap" :wrap="false" :size="20">
                                <table>    
                                    <thead>
                                        <th>초등학교 4학년</th>  
                                        <th>초등학교 6학년</th>   
                                        <th>중학교 2학년</th>  
                                        <th>고등학교 1학년</th>       
                                    </thead>                        
                                    <td>1000점~1700점</td>
                                    <td>1050점~1800점</td>   
                                    <td>1100점~1900점</td>
                                    <td>1150점~2000점</td>   
                                </table>
                                <table>    
                                    <thead>
                                        <th>초등학교 4학년</th>  
                                        <th>초등학교 6학년</th>   
                                        <th>중학교 2학년</th>  
                                        <th>고등학교 1학년</th> 
                                    </thead>                        
                                    <td>1000점~1700점</td>
                                    <td>1050점~1800점</td>   
                                    <td>1100점~1900점</td>
                                    <td>1150점~2000점</td>   
                                </table>
                            </n-flex>
                        </aside>
                    </div>                                        
                </n-flex>                
            </div>
        </div>
    </div>
</template> 
<style  lang="scss" scoped>
.bg-gary {
    position: relative;
    &::after {
        content: '';
        position: absolute;
        top: 0;
        left: -50px;
        width: calc(100% + 100px);
        z-index: -1;
        padding: 0 50px;
        background-color:#FAFAFA;
        height: calc(100% + 20px);
        min-height: 500px;
        border-bottom-right-radius: 30px;
        border-bottom-left-radius: 30px;
    }
    aside {
        .table-wrap {
            font-size: 14px;          
            table {
                border-radius: 5px;
                &:nth-child(1) th {background-color: var(--result-light-blue);}
                &:nth-child(2) th {background-color:var(--result-light-red);}
            } 
            th {color: #fff; font-weight: 500;}
            th, td {padding: 3px 0; text-align: center; border: 1px solid #ccc;} 
        }
    }
}
</style>